import { __assign, __read } from "tslib";
import { outlier } from './outlier';
var PluginPerformanceReport = /** @class */ (function () {
    function PluginPerformanceReport(entry) {
        var _this = this;
        this.entry = entry;
        this.count = 0;
        this.pluginNames = [];
        this.nodes = {};
        this.nodesDuration = 0;
        this.plugins = {};
        this.slowPlugins = {};
        this.options = {
            samplingRate: 100,
            slowThreshold: 300,
            outlierThreshold: 30,
            outlierFactor: 3,
        };
        this.isChild = function (child) {
            return child.startTime >= _this.entry.startTime &&
                child.startTime + child.duration <=
                    _this.entry.startTime + _this.entry.duration;
        };
    }
    PluginPerformanceReport.fromEntry = function (entry) {
        return new PluginPerformanceReport(entry);
    };
    PluginPerformanceReport.prototype.getEntryByName = function (entryList, name) {
        return entryList.getEntriesByName(name).find(this.isChild);
    };
    PluginPerformanceReport.prototype.getMethodSum = function (methods) {
        return Object.values(methods).reduce(function (a, b) { return a + b; }, 0);
    };
    PluginPerformanceReport.prototype.greaterEquals = function (a, b) {
        return typeof b === 'number' ? a >= b : false;
    };
    PluginPerformanceReport.prototype.hasOutlierMethods = function (methods, outliers) {
        return (this.greaterEquals(methods.stateApply, outliers.stateApplyOutlier) ||
            this.greaterEquals(methods.viewUpdate, outliers.viewUpdateOutlier));
    };
    Object.defineProperty(PluginPerformanceReport.prototype, "trigger", {
        get: function () {
            if (this.entry.duration > this.options.slowThreshold) {
                return 'slow';
            }
            else if (this.hasSlowPlugins &&
                this.entry.duration > this.options.outlierThreshold) {
                return 'distribution';
            }
            else if (this.count > 0 && this.count % this.options.samplingRate === 0) {
                return 'sample';
            }
            else {
                return 'none';
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PluginPerformanceReport.prototype, "hasSlowPlugins", {
        get: function () {
            return Object.keys(this.slowPlugins).length > 0;
        },
        enumerable: true,
        configurable: true
    });
    PluginPerformanceReport.prototype.withEntryList = function (entryList) {
        this.entryList = entryList;
        this.stateApplied = this.getEntryByName(entryList, '🦉 EditorView::state::apply');
        this.viewUpdated = this.getEntryByName(entryList, '🦉 EditorView::updateState');
        this.onChangeCalled = this.getEntryByName(entryList, '🦉 ReactEditorView::onChange');
        this.onEditorViewStateUpdatedCalled = this.getEntryByName(entryList, '🦉 ReactEditorView::onEditorViewStateUpdated');
        this.withPlugins(this.pluginNames);
        return this;
    };
    PluginPerformanceReport.prototype.withPlugins = function (pluginNames) {
        var _this = this;
        var emptyEntry = { duration: 0 };
        this.pluginNames = pluginNames;
        this.plugins = pluginNames.reduce(function (acc, name) {
            var pluginApplied = _this.entryList
                ? _this.getEntryByName(_this.entryList, "\uD83E\uDD89" + name + "::apply") || emptyEntry
                : emptyEntry;
            var pluginViewUpdated = _this.entryList
                ? _this.getEntryByName(_this.entryList, "\uD83E\uDD89" + name + "::view::update") ||
                    emptyEntry
                : emptyEntry;
            var pluginOnEditorViewStateUpdated = _this.entryList
                ? _this.getEntryByName(_this.entryList, "\uD83E\uDD89" + name + "::onEditorViewStateUpdated") || emptyEntry
                : emptyEntry;
            acc[name] = {
                stateApply: pluginApplied.duration,
                viewUpdate: pluginViewUpdated.duration,
                onEditorViewStateUpdated: pluginOnEditorViewStateUpdated.duration,
            };
            return acc;
        }, {});
        if (this.stateApplied && pluginNames.length > 0) {
            var pluginEntries = Object.entries(this.plugins);
            var stateApplyOutlier_1 = outlier(pluginEntries.map(function (_a) {
                var _b = __read(_a, 2), stateApply = _b[1].stateApply;
                return stateApply;
            }), this.options.outlierFactor);
            var viewUpdateOutlier_1 = outlier(pluginEntries.map(function (_a) {
                var _b = __read(_a, 2), viewUpdate = _b[1].viewUpdate;
                return viewUpdate;
            }), this.options.outlierFactor);
            var budget_1 = this.options.outlierThreshold / pluginEntries.length;
            /**
             * Consider plugin methods slow that are
             * statistically significantly slower than peers
             * AND where the sum of methods for a plugin is slower than 16.7ms / plugins.length
             */
            var pluginIsOutlier = function (_a) {
                var _b = __read(_a, 2), methods = _b[1];
                return _this.getMethodSum(methods) > budget_1 &&
                    _this.hasOutlierMethods(methods, {
                        stateApplyOutlier: stateApplyOutlier_1,
                        viewUpdateOutlier: viewUpdateOutlier_1,
                    });
            };
            this.slowPlugins = pluginEntries
                .filter(pluginIsOutlier)
                .reduce(function (acc, _a) {
                var _b;
                var _c = __read(_a, 2), n = _c[0], d = _c[1];
                return (__assign(__assign({}, acc), (_b = {}, _b[n] = d, _b)));
            }, {});
        }
        return this;
    };
    PluginPerformanceReport.prototype.withNodes = function (nodes, nodesDuration) {
        if (nodesDuration === void 0) { nodesDuration = 0; }
        this.nodes = nodes;
        this.nodesDuration = nodesDuration;
        return this;
    };
    PluginPerformanceReport.prototype.withCount = function (count) {
        this.count = count;
        return this;
    };
    PluginPerformanceReport.prototype.withOptions = function (options) {
        Object.assign(this.options, options);
        return this;
    };
    PluginPerformanceReport.prototype.toJSON = function () {
        return {
            trigger: this.trigger,
            duration: this.entry.duration,
            nodes: this.nodes,
            plugins: this.plugins,
            slowPlugins: this.slowPlugins,
            stepDurations: {
                stateApply: this.stateApplied ? this.stateApplied.duration : 0,
                viewUpdate: this.viewUpdated ? this.viewUpdated.duration : 0,
                onChange: this.onChangeCalled ? this.onChangeCalled.duration : 0,
                onEditorViewStateUpdated: this.onEditorViewStateUpdatedCalled
                    ? this.onEditorViewStateUpdatedCalled.duration
                    : 0,
                countNodes: this.nodesDuration,
            },
        };
    };
    return PluginPerformanceReport;
}());
export { PluginPerformanceReport };
//# sourceMappingURL=plugin-performance-report.js.map